import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const STYLES = ['flat', 'hobbled', 'relaxed', 'cascade', 'aventura', 'pleated'];
const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const STATUS = {
  ready: ['Ready', 'bg-green-100 text-green-800'],
  more_checks: ['9/10 — 10 more prepared', 'bg-amber-100 text-amber-800'],
  manual_review: ['Manual review', 'bg-red-100 text-red-800'],
};
/* Spot-check unlock status per style (10/10 → Ready; 9/10 → 10 more, then ≥19/20; else manual review) */
export const ReadinessBadge = ({ v, style }) => {
  const [label, cls] = STATUS[v?.status] || [v?.checked ? `${v.checked}/${v.required} checked` : 'Not checked yet', 'bg-neutral-100 text-neutral-500'];
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${cls}`} data-testid={`readiness-badge-${style}`}>{label}</span>;
};

/* Spot-check report: agreement % by style + every "QC wrong" note (human judgments only) */
export const SpotCheckSummary = ({ api, headers, refreshKey }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${api}/qc/spot-check/summary`, { headers }).then(({ data }) => setD(data)).catch(() => {}), [api, headers]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const bulkPasses = async (style) => {
    if (!window.confirm(`Approve every pending QC Pass ${style} scene and make it live?\n\nBorderline, Fail and QC-wrong scenes are excluded automatically.`)) return;
    try {
      const { data } = await axios.post(`${api}/qc/bulk-approve-passes`, { style }, { headers });
      toast.success(`${data.approved} ${style} QC passes approved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk approval failed'); }
  };
  if (!d) return null;
  return (
    <Dialog onOpenChange={(o) => { if (o) load(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" data-testid="spot-summary-btn">
          <ClipboardList className="w-3 h-3" />Spot-check summary · {d.done}/{d.total}{d.complete ? ' ✓' : ''}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Spot-check summary</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500 -mt-2" data-testid="spot-summary-status">
          {d.complete ? 'All prepared sets checked.' : `${d.done} of ${d.total} checked — finish all sets before deciding on bulk approval.`}
          {d.overall_agreement_pct != null && <> Overall agreement: <b>{d.overall_agreement_pct}%</b>.</>}
          <br />Rule: {d.rule}. {d.complete ? 'Spot-check complete — bulk approval (QC Pass only; Borderline, Fail and QC-wrong excluded) is available for Ready styles.' : 'Bulk approval is locked until every style is Ready or Manual review.'}
        </p>
        <table className="w-full text-xs" data-testid="spot-summary-table">
          <thead><tr className="text-left text-neutral-500 border-b"><th className="py-1">Style</th><th>Checked</th><th>Looks right</th><th>QC wrong</th><th>Agreement</th><th>Status</th><th /></tr></thead>
          <tbody>
            {STYLES.map(s => {
              const v = d.styles[s] || {};
              return (
                <tr key={s} className="border-b border-neutral-100" data-testid={`spot-summary-row-${s}`}>
                  <td className="py-1 capitalize font-medium">{s}</td>
                  <td>{v.checked}/{v.required}</td>
                  <td className="text-green-700">{v.looks_right}</td>
                  <td className="text-red-700">{v.qc_wrong}</td>
                  <td className="font-semibold">{v.agreement_pct == null ? '—' : `${v.agreement_pct}%`}</td>
                  <td><ReadinessBadge v={v} style={s} /></td>
                  <td>{d.complete && v.ready && <Button size="sm" className="h-6 text-[10px] bg-green-700 hover:bg-green-800" onClick={() => bulkPasses(s)} data-testid={`bulk-approve-passes-${s}`}>Approve QC passes</Button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-sm font-medium text-neutral-900 mt-2">"QC wrong" notes ({d.notes.length})</p>
        <div className="divide-y divide-neutral-100" data-testid="spot-summary-notes">
          {d.notes.length === 0 ? <p className="text-xs text-neutral-400 py-1">None yet.</p> : d.notes.map(n => (
            <div key={n.id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`spot-note-${n.id}`}>
              <a href={abs(n.url)} target="_blank" rel="noreferrer"><img src={abs(n.url)} alt="" className="w-12 h-12 object-cover rounded" /></a>
              <span className="capitalize text-neutral-500 w-16">{n.style}</span>
              <span className="font-medium text-neutral-800 w-40 truncate">{n.color_name} · {n.collection_name}</span>
              <span className="text-neutral-700 flex-1">{n.note || <i className="text-neutral-400">no note</i>}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
